import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography, Alert, Button, Paper, Chip, Avatar, Skeleton } from '@mui/material';
import { fetchEncounterById, updateEncounter, setSelectedEncounter, setRecentEncounters } from '../../redux/slices/encountersSlice';
import { setSelectedPatient } from '../../redux/slices/patientsSlice';
import { setPrescriptions } from '../../redux/slices/prescriptionsSlice';
import { setCareTeam } from '../../services/careCoordinationService';
import { getEncounterFull } from '../../services/encounterService';
import EncounterBaseRefactored from './EncounterBaseRefactored';
import SurgicalEncounterDetails from './surgical/SurgicalEncounterDetails';
import InpatientEncounterDetails from './inpatient/InpatientEncounterDetails';
import ObservationEncounterDetails from './observation/ObservationEncounterDetails';
import EmergencyEncounterDetails from './emergency/EmergencyEncounterDetails';
import OutpatientEncounterDetails from './outpatient/OutpatientEncounterDetails';
import TelehealthEncounterDetails from './telehealth/TelehealthEncounterDetails';
import WorkflowTaskList from './WorkflowTaskList';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { EncounterType } from '../../types/enums';
import './EncounterDetails.css';
dayjs.extend(utc);
dayjs.extend(timezone);

// Patient Info Ribbon Component
const PatientInfoRibbon = ({ patient, encounter, onPatientClick }) => {
  if (!patient) return null;

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'Unknown';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return 'Not specified';
    try {
      const date = new Date(dateOfBirth);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getInitials = (name, firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Paper sx={{ 
      p: 2, 
      mb: 2, 
      bgcolor: 'primary.50', 
      border: '1px solid', 
      borderColor: 'primary.200',
      borderRadius: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar 
          onClick={onPatientClick}
          title="View Patient Profile"
          sx={{ 
            width: 48, 
            height: 48, 
            bgcolor: 'primary.main',
            fontSize: '1.2rem',
            fontWeight: 600,
            cursor: onPatientClick ? 'pointer' : 'default',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': onPatientClick ? {
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            } : {},
          }}
        >
          {getInitials(patient.name, patient.firstName, patient.lastName)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h6" 
            onClick={onPatientClick}
            sx={{ 
              fontWeight: 600, 
              mb: 0.5,
              cursor: onPatientClick ? 'pointer' : 'default',
              '&:hover': onPatientClick ? {
                color: 'primary.main',
                textDecoration: 'underline',
              } : {},
            }}
          >
            {patient.firstName && patient.lastName 
              ? `${patient.firstName} ${patient.lastName}` 
              : patient.name || 'Unknown Patient'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {patient.mrn && (
              <Chip 
                label={patient.mrn.startsWith('MRN') ? patient.mrn : `MRN: ${patient.mrn}`} 
                size="small" 
                variant="outlined"
              />
            )}
            {encounter?.fin && (
              <Chip 
                label={encounter.fin.startsWith('FIN') ? encounter.fin : `FIN: ${encounter.fin}`} 
                size="small" 
                variant="outlined"
                color="secondary"
              />
            )}
            {patient.dateOfBirth && (
              <Chip 
                label={`DOB: ${formatDateOfBirth(patient.dateOfBirth)}`} 
                size="small" 
                variant="outlined"
              />
            )}
            {patient.dateOfBirth && (
              <Chip 
                label={`Age: ${calculateAge(patient.dateOfBirth)} years`} 
                size="small" 
                variant="outlined"
                color="primary"
              />
            )}
            {patient.gender && (
              <Chip 
                label={patient.gender} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const EncounterDetails = () => {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state - used for components that depend on Redux
  const encounter = useSelector(state => state.encounters.selectedEncounter);
  const selectedPatient = useSelector(state => state.patients.selectedPatient);
  const prescriptions = useSelector(state => state.prescriptions.prescriptions);
  const recentEncounters = useSelector(state => state.encounters.recentEncounters);
  const error = useSelector(state => state.encounters.error);
  
  // Local state
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [careTeamChanges, setCareTeamChanges] = useState(null);
  const [resetPendingChanges, setResetPendingChanges] = useState(() => () => {});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [dataFetchError, setDataFetchError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Track which encounter we've already fetched to prevent repeated calls
  const [fetchedEncounterId, setFetchedEncounterId] = useState(null);
  const dataLoadTimeoutRef = useRef(null);

  // Simplified loading state - only show loading on initial load
  const isLoading = !initialDataLoaded && !isRefreshing;
  
  // Single aggregated fetch - loads encounter + patient + prescriptions + recentEncounters in one API call
  useEffect(() => {
    if (!encounterId || encounterId === fetchedEncounterId) {
      return;
    }

    // Set a fallback timeout to prevent infinite loading
    dataLoadTimeoutRef.current = setTimeout(() => {
      console.warn('EncounterDetails: Data loading timeout triggered after 10 seconds');
      setDataFetchError('Data loading timed out. Some information may be incomplete.');
      setInitialDataLoaded(true);
    }, 10000); // 10 second timeout (reduced from 30s since we're using aggregated endpoint)

    const fetchFullEncounterData = async () => {
      try {
        console.log('EncounterDetails: Fetching aggregated encounter data for:', encounterId);
        
        // Single API call to get all core data
        const { encounter: encounterData, patient, prescriptions: rxData, recentEncounters: recentData } = await getEncounterFull(encounterId);
        
        // Update Redux state so child components work correctly
        dispatch(setSelectedEncounter(encounterData));
        
        if (patient) {
          dispatch(setSelectedPatient(patient));
        }
        
        if (rxData && rxData.length > 0) {
          dispatch(setPrescriptions(rxData));
        }
        
        if (recentData && recentData.length > 0) {
          dispatch(setRecentEncounters(recentData));
        }
        
        // Clear timeout on successful completion
        if (dataLoadTimeoutRef.current) {
          clearTimeout(dataLoadTimeoutRef.current);
          dataLoadTimeoutRef.current = null;
        }
        
        setFetchedEncounterId(encounterId);
        setInitialDataLoaded(true);
        setDataFetchError(null);
        
        console.log('EncounterDetails: Aggregated data loaded successfully');
      } catch (error) {
        console.error('Error fetching full encounter data:', error);
        
        // Clear timeout even on error
        if (dataLoadTimeoutRef.current) {
          clearTimeout(dataLoadTimeoutRef.current);
          dataLoadTimeoutRef.current = null;
        }
        
        setDataFetchError('Failed to load encounter data. Please try again.');
        setInitialDataLoaded(true);
      }
    };

    fetchFullEncounterData();

    return () => {
      if (dataLoadTimeoutRef.current) {
        clearTimeout(dataLoadTimeoutRef.current);
        dataLoadTimeoutRef.current = null;
      }
    };
  }, [encounterId, fetchedEncounterId, dispatch]);

  // Initialize formData from encounter when loaded
  useEffect(() => {
    if (encounter && !formData) { // Only initialize once when formData is null
      setFormData({
        ...encounter,
        // Ensure facilityId is always present
        facilityId: encounter.facilityId || selectedPatient?.facilityId || '',
        vitals: encounter.vitals || {
          bloodPressure: '',
          heartRate: '',
          temperature: '',
          respiratoryRate: '',
          oxygenSaturation: '',
          weight: '',
          height: '',
          bmi: ''
        },
        notes: {
          consultationNotes: '',
          secondOpinionNotes: '',
          progressNotes: '',
          nurseNotes: '',
          procedureNotes: '',
          soapNotes: {
            subjective: '',
            objective: '',
            assessment: '',
            plan: ''
          },
          simpleNotes: '',
          phoneNotes: ''
        },
        symptoms: encounter.symptoms || [],
        actionPlan: encounter.actionPlan || '',
        provider: encounter.provider || {},
        department: encounter.department || '',
        unit: encounter.unit || '',
        chiefComplaint: encounter.chiefComplaint || '',
        initialAssessment: encounter.initialAssessment || ''
      });
    }
  }, [encounter?.id, selectedPatient?.facilityId, formData, encounter]);

  // Reset fetch tracking when encounter ID changes
  useEffect(() => {
    if (encounterId && encounterId !== fetchedEncounterId) {
      setInitialDataLoaded(false);
      setDataFetchError(null);
      setFormData(null);
    }
  }, [encounterId, fetchedEncounterId]);

  // Function to refresh encounter data (for bed assignment updates)
  const refreshEncounterData = useCallback(async () => {
    // Use encounter?.id if available, otherwise fall back to URL encounterId
    const encounterIdToRefresh = encounter?.id || encounterId;
    
    if (encounterIdToRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        console.log('Refreshing encounter data for:', encounterIdToRefresh);
        
        // Use aggregated endpoint for refresh as well
        const { encounter: encounterData, patient, prescriptions: rxData, recentEncounters: recentData } = await getEncounterFull(encounterIdToRefresh);
        
        // Update Redux state
        dispatch(setSelectedEncounter(encounterData));
        
        if (patient) {
          dispatch(setSelectedPatient(patient));
        }
        
        if (rxData && rxData.length > 0) {
          dispatch(setPrescriptions(rxData));
        }
        
        if (recentData && recentData.length > 0) {
          dispatch(setRecentEncounters(recentData));
        }
        
        // Force formData re-initialization for all encounter types
        // This ensures that bed assignment updates are reflected in the UI
        console.log('Forcing formData re-initialization after encounter refresh');
        setFormData(null);
        
      } catch (error) {
        console.error('Error refreshing encounter data:', error);
        setDataFetchError('Failed to refresh encounter data');
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [dispatch, encounterId, encounter?.id, isRefreshing]);

  const displayDate = useMemo(() => {
    let date = encounter?.createdAt ? new Date(encounter.createdAt) : new Date();
    if (isNaN(date.getTime())) date = new Date();
    const userTz = dayjs.tz.guess();
    return dayjs(date).tz(userTz).format('YYYY-MM-DD HH:mm:ss');
  }, [encounter?.createdAt]);

  const handleSave = useCallback(async () => {
    if (!formData) return;
    
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        createdAt: new Date().toISOString(), // Always set to current UTC time
        vitals: formData.vitals,
        notes: formData.notes,
        symptoms: formData.symptoms,
        actionPlan: formData.actionPlan,
        provider: formData.provider,
        department: formData.department,
        unit: formData.unit,
        chiefComplaint: formData.chiefComplaint,
        initialAssessment: formData.initialAssessment
      };
      
      // Sanitize the update data before sending to backend
      const sanitizedUpdateData = JSON.parse(JSON.stringify(updateData));
      
      // Save encounter data
      await dispatch(updateEncounter({ encounterId: encounter.id, updateData: sanitizedUpdateData })).unwrap();
      
      // Save care team changes if any
      if (careTeamChanges && encounter?.patientId) {
        await setCareTeam(encounter.patientId, careTeamChanges, 'system');
        setCareTeamChanges(null); // Clear changes after saving
      }
      
      // Reset pending changes state
      resetPendingChanges();
    } catch (error) {
      console.error('Error saving encounter:', error);
    } finally {
      setSaving(false);
    }
  }, [formData, dispatch, encounter?.id, encounter?.patientId, careTeamChanges, resetPendingChanges]);

  const handleBack = useCallback(() => {
    navigate('/encounters');
  }, [navigate]);

  const renderTaskList = useCallback(() => (
    <WorkflowTaskList encounterId={encounter?.id} />
  ), [encounter?.id]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading encounter details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '400px', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  if (!encounter) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>No encounter found</Typography>
      </Box>
    );
  }

  // Render encounter component based on type
  let encounterComponent;
  switch (encounter.type) {
    case EncounterType.SURGERY:
      encounterComponent = <SurgicalEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    case EncounterType.INPATIENT_ADMISSION:
      encounterComponent = <InpatientEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    case EncounterType.OBSERVATION:
      encounterComponent = <ObservationEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    case EncounterType.EMERGENCY_VISIT:
      encounterComponent = <EmergencyEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    case EncounterType.OUTPATIENT_VISIT:
      encounterComponent = <OutpatientEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    case EncounterType.TELEHEALTH:
      encounterComponent = <TelehealthEncounterDetails key={encounter.id} onRefreshEncounter={refreshEncounterData} />;
      break;
    default:
      // Fallback to EncounterBaseRefactored for unknown types or legacy encounters
      encounterComponent = (
        <EncounterBaseRefactored
          key={encounter.id}
          mode="edit"
          encounter={encounter}
          patient={selectedPatient}
          prescriptions={prescriptions}
          recentEncounters={recentEncounters}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          onBack={handleBack}
          loading={isLoading}
          saving={saving}
          title={`Encounter Details - Admission Date/Time: ${displayDate}`}
          renderTaskList={renderTaskList}
          showExtraTabs={true}
          facilityId={encounter?.facilityId || selectedPatient?.facilityId || ''}
          encounterType={encounter?.type}
          onCareTeamChange={setCareTeamChanges}
          onSaveSuccess={setResetPendingChanges}
          onRefreshEncounter={refreshEncounterData}
          showPatientInfoInHeader={false}
        />
      );
  }

  return (
    <>
      {dataFetchError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {dataFetchError}
        </Alert>
      )}
      {selectedPatient && (
        <PatientInfoRibbon 
          patient={selectedPatient} 
          encounter={encounter} 
          onPatientClick={() => navigate(`/patients/${selectedPatient.id}`)}
        />
      )}
      {encounterComponent}
    </>
  );
};

export default EncounterDetails;
